import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getMyProfile } from '@/services/operatorService'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ROUTES } from '@/constants/routes'

interface Props {
  children: ReactNode
}

// Path consentiti durante l'onboarding (prima che `onboardingStep === 'PUBLISHED'`).
// Tutto ciò che non rientra qui viene reindirizzato a Dashboard.
//
// Include il profilo professionale — sia il wizard (/operator/profile/wizard,
// compilazione candidatura) sia l'editor (/operator/profile/edit, usato dalla
// CTA "Modifica il profilo e re-invia" dopo una richiesta di revisione) — e
// l'editor articoli (gate finale "≥1 articolo PUBLISHED" pre-PUBLISHED). Senza
// questi prefissi le CTA "Compila" / "Modifica" / "Scrivi articolo"
// rimbalzerebbero a Dashboard.
const ONBOARDING_ALLOWED_PREFIXES = [
  '/operator/dashboard',
  '/operator/onboarding',
  '/operator/profile',
  '/operator/articles',
  // Gate go-live "≥1 servizio a pagamento": l'operatore deve poter creare i
  // servizi durante l'onboarding (CTA dalla checklist).
  '/operator/services',
  // La guida operatore spiega anche il percorso di attivazione: consultabile
  // fin dal primo accesso.
  '/operator/guide',
] as const

function isAllowedDuringOnboarding(pathname: string): boolean {
  return ONBOARDING_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

/**
 * Limita l'operatore a Dashboard + pagine onboarding finché il profilo non è
 * PUBLISHED. Operatori legacy (senza `onboardingStep` — flusso pre-M2)
 * non vengono limitati: hanno già il loro flusso vecchio.
 *
 * Va wrappato DENTRO ProtectedRoute + RoleRoute (vedi `routes/index.tsx`).
 */
export function OnboardingGate({ children }: Props) {
  const location = useLocation()
  const { data: profile, isLoading } = useQuery({
    queryKey: ['operator', 'profile', 'me'],
    queryFn:  getMyProfile,
    // 404 = profilo non ancora creato (es. operatore appena invitato senza PROFILE su DB).
    // In tal caso `data` è undefined: gate "soft" → solo Dashboard/onboarding accessibili.
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const onboardingStep = profile?.onboardingStep
  const isLegacy = profile && !onboardingStep   // profilo esistente pre-M2 → bypass
  const isPublished = onboardingStep === 'PUBLISHED'

  if (isLegacy || isPublished) return <>{children}</>

  // Pre-PUBLISHED: solo Dashboard + onboarding/*. Tutto il resto redirige.
  if (!isAllowedDuringOnboarding(location.pathname)) {
    return <Navigate to={ROUTES.OPERATOR.DASHBOARD} replace />
  }

  return <>{children}</>
}
